import { useEffect } from "react";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --navy: #0A1628;
    --blue: #1B6CA8;
    --teal: #0EA5C9;
    --orange: #F4845F;
    --white: #FFFFFF;
    --offwhite: #F7F9FC;
    --text: #1A2B3C;
    --muted: #64748B;
  }

  body { font-family: 'DM Sans', sans-serif; background: var(--offwhite); color: var(--text); }

  .vi-hero {
    background: var(--navy);
    position: relative;
    overflow: hidden;
    padding: 72px 48px 64px;
  }
  .vi-hero::before {
    content: '';
    position: absolute;
    top: -120px; right: -120px;
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(27,108,168,0.3) 0%, transparent 70%);
    pointer-events: none;
  }
  .vi-hero::after {
    content: '';
    position: absolute;
    bottom: -80px; left: -80px;
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(244,132,95,0.15) 0%, transparent 70%);
    pointer-events: none;
  }
  .vi-hero-inner { max-width: 900px; margin: 0 auto; position: relative; z-index: 1; }
  .vi-badge {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 20px; padding: 6px 16px;
    font-size: 11px; font-weight: 500; color: rgba(255,255,255,0.6);
    letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 28px;
  }
  .vi-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--teal); }
  .vi-hero-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(2.4rem, 5vw, 3.8rem);
    font-weight: 700; color: var(--white);
    line-height: 1.08; letter-spacing: -0.02em; margin-bottom: 8px;
  }
  .vi-hero-title span { color: var(--orange); }
  .vi-hero-sub {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.1rem, 2.5vw, 1.5rem);
    font-weight: 400; font-style: italic;
    color: rgba(255,255,255,0.45); margin-bottom: 28px;
  }
  .vi-hero-desc {
    font-size: 1rem; font-weight: 300; color: rgba(255,255,255,0.6);
    line-height: 1.75; max-width: 600px; margin-bottom: 40px;
  }
  .vi-ctas { display: flex; gap: 16px; flex-wrap: wrap; }
  .vi-btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--orange); color: var(--white); text-decoration: none;
    font-size: 0.9rem; font-weight: 500; padding: 13px 28px;
    border-radius: 4px; transition: all 0.2s;
  }
  .vi-btn-primary:hover { background: #e06b44; transform: translateY(-1px); }
  .vi-btn-secondary {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; color: rgba(255,255,255,0.7); text-decoration: none;
    font-size: 0.9rem; font-weight: 400; padding: 13px 28px;
    border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; transition: all 0.2s;
  }
  .vi-btn-secondary:hover { border-color: rgba(255,255,255,0.5); color: var(--white); }

  .vi-stats-bar { background: var(--blue); padding: 28px 48px; }
  .vi-stats-inner {
    max-width: 900px; margin: 0 auto;
    display: grid; grid-template-columns: repeat(4, 1fr);
  }
  .vi-stat { text-align: center; padding: 0 16px; border-right: 1px solid rgba(255,255,255,0.15); }
  .vi-stat:last-child { border-right: none; }
  .vi-stat-value {
    font-family: 'Playfair Display', serif;
    font-size: 2rem; font-weight: 700; color: var(--white); line-height: 1; margin-bottom: 4px;
  }
  .vi-stat-label { font-size: 0.72rem; color: rgba(255,255,255,0.6); letter-spacing: 0.05em; }

  .vi-main { max-width: 900px; margin: 0 auto; padding: 64px 48px; }

  .vi-products { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 64px; }
  .vi-product-card { border-radius: 8px; overflow: hidden; border: 1px solid rgba(0,0,0,0.08); }
  .vi-product-header { padding: 28px 28px 20px; }
  .vi-product-header.blue { background: var(--navy); }
  .vi-product-header.teal { background: #0A2438; }
  .vi-product-icon { font-size: 24px; margin-bottom: 12px; display: block; }
  .vi-product-name { font-family: 'Playfair Display', serif; font-size: 1.5rem; font-weight: 700; color: var(--white); margin-bottom: 4px; }
  .vi-product-name span { color: var(--orange); }
  .vi-product-tagline { font-size: 0.78rem; color: rgba(255,255,255,0.45); font-style: italic; }
  .vi-product-body { padding: 24px 28px; background: var(--white); }
  .vi-product-desc { font-size: 0.88rem; font-weight: 300; color: var(--muted); line-height: 1.7; margin-bottom: 20px; }
  .vi-feature-list { list-style: none; display: flex; flex-direction: column; gap: 10px; }
  .vi-feature-item { display: flex; align-items: flex-start; gap: 10px; font-size: 0.83rem; color: var(--text); line-height: 1.5; }
  .vi-feature-dot {
    width: 18px; height: 18px; border-radius: 50%; background: var(--blue);
    color: white; font-size: 10px; display: flex; align-items: center;
    justify-content: center; flex-shrink: 0; margin-top: 1px;
  }
  .vi-feature-dot.orange { background: var(--orange); }
  .vi-product-link {
    display: inline-flex; align-items: center; gap: 6px; margin-top: 20px;
    font-size: 0.82rem; font-weight: 500; color: var(--blue); text-decoration: none;
    border-bottom: 1px solid rgba(27,108,168,0.3); padding-bottom: 2px; transition: all 0.15s;
  }
  .vi-product-link:hover { color: var(--orange); border-color: var(--orange); }

  .vi-section-label {
    font-size: 0.68rem; font-weight: 500; letter-spacing: 0.18em;
    text-transform: uppercase; color: var(--muted); margin-bottom: 16px;
  }
  .vi-section-title {
    font-family: 'Playfair Display', serif; font-size: 1.8rem; font-weight: 700;
    color: var(--navy); margin-bottom: 12px; line-height: 1.2;
  }
  .vi-section-body {
    font-size: 0.92rem; font-weight: 300; color: var(--muted);
    line-height: 1.75; margin-bottom: 32px; max-width: 640px;
  }

  .vi-focus-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 64px; }
  .vi-focus-card {
    background: var(--white); border: 1px solid rgba(0,0,0,0.07);
    border-radius: 6px; padding: 24px 20px; border-top: 3px solid var(--blue);
  }
  .vi-focus-card.orange { border-top-color: var(--orange); }
  .vi-focus-card.teal { border-top-color: var(--teal); }
  .vi-focus-icon { font-size: 20px; margin-bottom: 12px; }
  .vi-focus-title { font-size: 0.9rem; font-weight: 600; color: var(--navy); margin-bottom: 8px; }
  .vi-focus-body { font-size: 0.78rem; font-weight: 300; color: var(--muted); line-height: 1.6; }

  .vi-help-section { background: var(--navy); border-radius: 8px; padding: 40px; margin-bottom: 48px; }
  .vi-help-title { font-family: 'Playfair Display', serif; font-size: 1.5rem; font-weight: 700; color: var(--white); margin-bottom: 8px; }
  .vi-help-sub { font-size: 0.85rem; font-weight: 300; color: rgba(255,255,255,0.5); margin-bottom: 28px; line-height: 1.6; }
  .vi-help-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .vi-help-item { display: flex; gap: 14px; align-items: flex-start; }
  .vi-help-num {
    width: 28px; height: 28px; border-radius: 50%; background: var(--orange);
    color: white; font-size: 12px; font-weight: 600;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .vi-help-text { font-size: 0.83rem; color: rgba(255,255,255,0.65); line-height: 1.6; }
  .vi-help-text strong { color: var(--white); font-weight: 500; }

  .vi-cta-section {
    background: linear-gradient(135deg, var(--blue), #0A4A7A);
    border-radius: 8px; padding: 48px 40px; text-align: center; margin-bottom: 48px;
  }
  .vi-cta-title { font-family: 'Playfair Display', serif; font-size: 1.8rem; font-weight: 700; color: var(--white); margin-bottom: 12px; }
  .vi-cta-body {
    font-size: 0.92rem; font-weight: 300; color: rgba(255,255,255,0.65);
    line-height: 1.7; margin-bottom: 28px; max-width: 480px; margin-left: auto; margin-right: auto;
  }
  .vi-cta-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }

  .vi-footer { text-align: center; padding: 0 0 48px; }
  .vi-footer-logo { font-family: 'Playfair Display', serif; font-size: 1.2rem; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
  .vi-footer-logo span { color: var(--orange); }
  .vi-footer-text { font-size: 0.78rem; color: var(--muted); line-height: 1.6; }
  .vi-footer-links { display: flex; gap: 24px; justify-content: center; margin-top: 12px; }
  .vi-footer-links a { font-size: 0.78rem; color: var(--blue); text-decoration: none; }
  .vi-footer-links a:hover { text-decoration: underline; }

  @media (max-width: 680px) {
    .vi-hero { padding: 48px 24px; }
    .vi-stats-inner { grid-template-columns: repeat(2,1fr); gap: 20px; }
    .vi-stat { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.15); padding-bottom: 16px; }
    .vi-main { padding: 40px 24px; }
    .vi-products { grid-template-columns: 1fr; }
    .vi-focus-grid { grid-template-columns: 1fr; }
    .vi-help-grid { grid-template-columns: 1fr; }
    .vi-stats-bar { padding: 28px 24px; }
  }
`;

export default function PlatformsPage() {
  useEffect(() => {
    document.title = "AmyloidConnect + NephroTrax · VeraInsight Healthcare";
  }, []);

  return (
    <>
      <style>{css}</style>

      {/* Hero */}
      <section className="vi-hero">
        <div className="vi-hero-inner">
          <div className="vi-badge">
            <div className="vi-badge-dot"></div>
            VeraInsight Healthcare · Rare Disease Technology
          </div>
          <h1 className="vi-hero-title">
            Connecting Patients<br/>to <span>Care</span>
          </h1>
          <p className="vi-hero-sub">Education · Clinical Trials · Travel Coordination</p>
          <p className="vi-hero-desc">
            VeraInsight Healthcare builds technology that removes barriers between patients
            and the treatments they need. AmyloidConnect and NephroTrax each educate patients,
            match them to clinical trials or specialists, and coordinate their travel — end to
            end, built directly into the platform rather than handed off elsewhere.
          </p>
          <div className="vi-ctas">
            <a href="https://amyloidconnect.com" className="vi-btn-primary" target="_blank" rel="noopener noreferrer">AmyloidConnect ↗</a>
            <a href="https://nephrotrax.com" className="vi-btn-secondary" target="_blank" rel="noopener noreferrer">NephroTrax ↗</a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="vi-stats-bar">
        <div className="vi-stats-inner">
          {[
            { value: "50k+", label: "Active clinical trials in the US" },
            { value: "30%",  label: "Patients drop out due to travel burden" },
            { value: "10y",  label: "Average ATTR-v diagnosis delay" },
            { value: "2",    label: "Platforms. One mission." },
          ].map((s) => (
            <div className="vi-stat" key={s.value}>
              <div className="vi-stat-value">{s.value}</div>
              <div className="vi-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <main className="vi-main">

        {/* Products */}
        <div className="vi-products">
          <div className="vi-product-card">
            <div className="vi-product-header blue">
              <span className="vi-product-icon">🔬</span>
              <div className="vi-product-name">Amyloid<span>Connect</span></div>
              <div className="vi-product-tagline">Patient education + clinical trial matching</div>
            </div>
            <div className="vi-product-body">
              <p className="vi-product-desc">
                A comprehensive platform helping amyloidosis patients understand their diagnosis,
                explore organ manifestations, calculate disease staging, and find matching clinical trials.
              </p>
              <ul className="vi-feature-list">
                {[
                  "Organ-specific education — cardiac, neuro, renal, GI, MSK, ophthalmic",
                  "Biomarker staging calculator — Mayo 2004, Mayo 2012, European",
                  "AI-powered clinical trial matching — AL, ATTR-v, ATTR-wt",
                  "Integrated travel booking — book flights and hotels for a trial visit directly from the trial card",
                ].map((f) => (
                  <li className="vi-feature-item" key={f}>
                    <span className="vi-feature-dot">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <a href="https://amyloidconnect.com" className="vi-product-link" target="_blank" rel="noopener noreferrer">
                Visit AmyloidConnect →
              </a>
            </div>
          </div>

          <div className="vi-product-card">
            <div className="vi-product-header teal">
              <span className="vi-product-icon">💧</span>
              <div className="vi-product-name">Nephro<span>Trax</span></div>
              <div className="vi-product-tagline">Chronic kidney disease care platform</div>
            </div>
            <div className="vi-product-body">
              <p className="vi-product-desc">
                A connected platform for CKD patients and the providers who care for them —
                daily health check-ins, automated clinical alerts, and a real-time provider
                dashboard, with travel booking built in for appointments.
              </p>
              <ul className="vi-feature-list">
                {[
                  "Provider dashboard with automated clinical alerts",
                  "Daily patient check-ins — vitals, symptoms, medication adherence",
                  "Epic EHR lab sync, with manual entry when needed",
                  "Integrated travel booking for appointments",
                ].map((f) => (
                  <li className="vi-feature-item" key={f}>
                    <span className="vi-feature-dot orange">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <a href="https://nephrotrax.com" className="vi-product-link" target="_blank" rel="noopener noreferrer">
                Visit NephroTrax →
              </a>
            </div>
          </div>
        </div>

        {/* Focus areas */}
        <p className="vi-section-label">Clinical focus areas</p>
        <h2 className="vi-section-title">Where amyloidosis burden is highest</h2>
        <p className="vi-section-body">
          Amyloidosis affects multiple organ systems. The highest-burden manifestations —
          cardiac, renal, and neurological — are also where clinical trials are most active
          and where early diagnosis has the greatest impact on outcomes.
        </p>
        <div className="vi-focus-grid">
          {[
            { cls: "", icon: "❤️", title: "Cardiac Amyloidosis", body: "Leading cause of death in AL. ATTR-CM affects millions of elderly patients. Tafamidis, patisiran, and novel agents in active trials. Early diagnosis is critical." },
            { cls: "orange", icon: "🧠", title: "Neurological & Dementia", body: "Transthyretin amyloid in the brain is increasingly linked to dementia. ATTR-v FAP causes progressive neuropathy. TTR silencers can halt progression if caught early." },
            { cls: "teal", icon: "🫘", title: "Renal Amyloidosis", body: "35% of AL patients present with renal involvement. Massive proteinuria and progressive renal failure. Successful AL treatment can stabilize or reverse kidney damage." },
          ].map((c) => (
            <div className={`vi-focus-card ${c.cls}`} key={c.title}>
              <div className="vi-focus-icon">{c.icon}</div>
              <div className="vi-focus-title">{c.title}</div>
              <div className="vi-focus-body">{c.body}</div>
            </div>
          ))}
        </div>

        {/* How we help */}
        <div className="vi-help-section">
          <h2 className="vi-help-title">How we support physicians and their patients</h2>
          <p className="vi-help-sub">
            AmyloidConnect and NephroTrax are designed to complement clinical care — not replace it.
            Physicians can refer patients to either platform as a free resource.
          </p>
          <div className="vi-help-grid">
            {[
              { num: "1", label: "Patient education", body: "Refer newly diagnosed patients to AmyloidConnect for organ-specific education and staging calculators — reducing time spent on basics in clinic." },
              { num: "2", label: "Trial awareness", body: "Patients can search and match to clinical trials directly — helping physicians who don't specialize in amyloidosis connect their patients to expert centers." },
              { num: "3", label: "Travel barrier removal", body: "Travel booking is built directly into each platform — a patient who finds a trial or specialist can book flights and hotels without leaving the page." },
              { num: "4", label: "Colleague network", body: "Share with rheumatology, cardiology, nephrology, and neurology colleagues — amyloidosis crosses all these specialties." },
            ].map((h) => (
              <div className="vi-help-item" key={h.num}>
                <div className="vi-help-num">{h.num}</div>
                <div className="vi-help-text"><strong>{h.label}:</strong> {h.body}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="vi-cta-section">
          <h2 className="vi-cta-title">Partner with us</h2>
          <p className="vi-cta-body">
            We are actively seeking physician advisors, patient advocacy partnerships,
            and clinical trial site collaborations. If you see an opportunity to work together,
            we would love to connect.
          </p>
          <div className="vi-cta-buttons">
            <a href="mailto:sandeep@verainsight.com" className="vi-btn-primary">Get in touch</a>
            <a href="/products" className="vi-btn-secondary">
              Explore Our Products
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="vi-footer">
          <div className="vi-footer-logo">Vera<span>Insight</span> Healthcare Inc.</div>
          <div className="vi-footer-text">
            Building technology for rare disease patients · Chicago, IL<br/>
            sandeep@verainsight.com · verainsight.com
          </div>
          <div className="vi-footer-links">
            <a href="https://amyloidconnect.com" target="_blank" rel="noopener noreferrer">AmyloidConnect</a>
            <a href="https://nephrotrax.com" target="_blank" rel="noopener noreferrer">NephroTrax</a>
            <a href="mailto:sandeep@verainsight.com">Contact</a>
          </div>
        </div>

      </main>
    </>
  );
}